import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { services } from '../data/services';
import PageSEO from '../components/PageSEO';
import { testimonials } from '../data/testimonials';
import blogArticles from '../data/blog';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const faqs = [
  {
    q: 'Comment fonctionne le devis gratuit ?',
    a: 'Notre devis est entièrement gratuit et sans engagement. Vous remplissez notre formulaire en ligne ou nous contactez par téléphone. Un de nos conseillers vous rappelle sous 24h pour préciser votre projet, puis un artisan se déplace pour évaluer le chantier et vous remettre un devis détaillé et transparent.',
  },
  {
    q: "Quels sont les délais d'intervention habituels ?",
    a: "Les délais varient selon la nature des travaux et la période. En général, nous pouvons intervenir sous 2 à 4 semaines pour les petits chantiers (salle de bain, isolation). Pour une rénovation complète ou une extension, comptez 4 à 8 semaines entre la signature du devis et le début des travaux.",
  },
  /* RGE FAQ — à rétablir après obtention qualif
  {
    q: "Êtes-vous certifiés RGE ? Qu'est-ce que cela implique ?",
    a: "Oui, Apex Rénovation est certifié RGE (Reconnu Garant de l'Environnement) par Qualibat. Cette certification vous permet de bénéficier des aides financières de l'État pour vos travaux de rénovation énergétique : MaPrimeRénov', éco-PTZ, TVA à taux réduit (5,5%) et les Certificats d'Économies d'Énergie (CEE).",
  },
  {
    q: "Puis-je bénéficier de MaPrimeRénov' pour mes travaux ?",
    a: "Si vos travaux concernent l'isolation, le chauffage ou la ventilation, vous pouvez dans la plupart des cas bénéficier de MaPrimeRénov'. En tant qu'entreprise certifiée RGE, nous vous accompagnons dans vos démarches administratives et pouvons même gérer le dossier à votre place.",
  },
  */
  {
    q: 'Quelle est la garantie sur les travaux réalisés ?',
    a: 'Tous nos travaux bénéficient de la garantie décennale obligatoire, qui couvre les dommages affectant la solidité de l\'ouvrage pendant 10 ans. Nous proposons également une garantie de parfait achèvement d\'un an et une garantie biennale de 2 ans sur les équipements.',
  },
  {
    q: 'Dans quelles communes intervenez-vous ?',
    a: "Nous intervenons dans tout le Berry : l'Indre (36) et le Cher (18), avec Châteauroux comme base. Nous couvrons notamment Issoudun, La Châtre, Argenton-sur-Creuse, Déols, Bourges, Vierzon, Saint-Amand-Montrond et toutes les communes alentours.",
  },
];

const recentProjects = [
  {
    title: 'Rénovation complète maison 120m²',
    type: 'Rénovation Intérieure',
    city: 'Châteauroux',
    image: 'https://images.unsplash.com/photo-1589939705384-5185137a7f0f?w=600&q=80',
  },
  {
    title: 'Cuisine contemporaine sur mesure',
    type: 'Cuisine',
    city: 'Issoudun',
    image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600&q=80',
  },
  {
    title: 'Salle de bain italienne',
    type: 'Salle de Bain',
    city: 'La Châtre',
    image: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=600&q=80',
  },
  {
    title: 'Isolation combles perdus',
    type: 'Isolation',
    city: 'Bourges',
    image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600&q=80',
  },
  {
    title: 'Ravalement façade crépi',
    type: 'Façade',
    city: 'Argenton-sur-Creuse',
    image: 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=600&q=80',
  },
  {
    title: 'Bureaux open space 200m²',
    type: 'Aménagement Bureau',
    city: 'Châteauroux',
    image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&q=80',
  },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-slate-50 transition-colors"
      >
        <span className="font-semibold text-slate-900 pr-4">{q}</span>
        <svg
          className={`w-5 h-5 text-orange-500 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="px-6 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">
          {a}
        </div>
      )}
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    propertyType: '',
    surface: '',
    etat: '',
    tvaKey: 'tva_10', // par défaut : travaux résidentiels >2 ans (cas le plus fréquent)
    codePostal: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Hero form = hook minimal. /devis lit location.state.prefill pour
    // pré-remplir propertyType/surface/état et enchaîner la suite.
    navigate('/devis', { state: { prefill: formData } });
  };

  return (
    <>
      <PageSEO
        path="/"
        description="Artisans rénovation maison et commerce à Châteauroux et dans l'Indre (36). Rénovation intérieure, cuisine, salle de bain, isolation, toiture, façade. Garantie décennale. Devis gratuit en ligne."
      />
      {/* ── HERO ── */}
      <section className="bg-slate-900 py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-600 opacity-10 rounded-full -translate-y-1/2 translate-x-1/4 blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            {/* Left */}
            <div>
              <div className="inline-flex items-center gap-2 bg-orange-600/20 text-orange-400 text-sm font-semibold px-4 py-2 rounded-full mb-6">
                <span className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" />
                Artisans rénovation en Indre
              </div>
              <h1 className="text-3xl lg:text-4xl xl:text-5xl font-extrabold text-white leading-tight mb-6">
                Rénovez votre maison ou commerce{' '}
                <span className="text-orange-500">en toute sérénité</span>
              </h1>
              <p className="text-slate-300 text-lg leading-relaxed mb-8">
                Apex Rénovation accompagne les particuliers et professionnels dans tous leurs projets de
                rénovation en Indre. Devis gratuit, artisans qualifiés, garantie décennale.
              </p>
              <div className="flex items-center gap-3 mb-8">
                <div className="flex">
                  {'★★★★★'.split('').map((_, i) => (
                    <span key={i} className="text-amber-400 text-lg">★</span>
                  ))}
                </div>
                <span className="text-white font-semibold">4,8/5</span>
                <span className="text-slate-400">par nos clients dans le Berry</span>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/devis"
                  className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-bold px-6 py-3 sm:px-8 sm:py-4 rounded-xl transition-all shadow-lg text-sm sm:text-base"
                >
                  Devis Gratuit →
                </Link>
                {/* Mobile: WhatsApp */}
                <a
                  href="https://wa.me/33759748656"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sm:hidden inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold px-6 py-3 rounded-xl transition-all text-sm"
                >
                  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.126 1.533 5.858L.054 23.445a.75.75 0 00.922.922l5.587-1.479A11.94 11.94 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.907 0-3.7-.506-5.248-1.391l-.378-.22-3.917 1.036 1.036-3.917-.22-.378A9.952 9.952 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
                  WhatsApp
                </a>
                {/* Desktop: Assistant IA */}
                <Link
                  to="/assistant"
                  className="hidden sm:inline-flex items-center gap-1.5 px-8 py-4 rounded-xl transition-all text-base font-semibold border text-orange-400 border-orange-400/50 hover:bg-orange-400/10"
                >
                  ✦ Assistant IA
                </Link>
              </div>
              <div className="flex flex-wrap gap-4 mt-8">
                {[/* '✓ Certifié RGE', */ '✓ Garantie décennale', '✓ Devis sans engagement'].map((t) => (
                  <span key={t} className="text-emerald-400 text-sm font-medium">{t}</span>
                ))}
              </div>
            </div>

            {/* Right: quick devis form */}
            <div>
              <div className="bg-white rounded-2xl shadow-2xl p-8">
                <h2 className="text-xl font-bold text-slate-900 mb-1">Devis gratuit en 2 min</h2>
                <p className="text-slate-500 text-sm mb-6">Votre estimation en ligne — Sans engagement</p>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Type de bien</label>
                      <div className="grid grid-cols-4 gap-2">
                        {[
                          { val: 'maison',           icon: '🏠', label: 'Maison' },
                          { val: 'appartement',      icon: '🏢', label: 'Appart.' },
                          { val: 'local_commercial', icon: '🏪', label: 'Local' },
                          { val: 'bureau_pro',       icon: '💼', label: 'Bureau' },
                        ].map(({ val, icon, label }) => (
                          <button
                            type="button"
                            key={val}
                            onClick={() => setFormData({ ...formData, propertyType: val })}
                            className={`p-2.5 rounded-lg border-2 text-center transition-all ${formData.propertyType === val ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-orange-300'}`}
                          >
                            <div className="text-xl">{icon}</div>
                            <div className="text-[11px] font-medium text-slate-700 mt-0.5">{label}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">Surface du bien (m²)</label>
                        <input
                          type="number"
                          className="w-full border border-slate-200 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                          placeholder="Ex. 80"
                          value={formData.surface}
                          onChange={(e) => setFormData({ ...formData, surface: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">Code postal</label>
                        <input
                          type="text"
                          className="w-full border border-slate-200 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                          placeholder="Ex. 36000"
                          value={formData.codePostal}
                          onChange={(e) => setFormData({ ...formData, codePostal: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">État du bien</label>
                        <select
                          className="w-full border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none bg-white"
                          value={formData.etat}
                          onChange={(e) => setFormData({ ...formData, etat: e.target.value })}
                          required
                        >
                          <option value="">État général</option>
                          <option value="neuf">Neuf / récent</option>
                          <option value="bon_etat">Bon état</option>
                          <option value="a_renover">À rénover</option>
                          <option value="mauvais">Vétuste</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">TVA applicable</label>
                        <select
                          className="w-full border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none bg-white"
                          value={formData.tvaKey}
                          onChange={(e) => setFormData({ ...formData, tvaKey: e.target.value })}
                          required
                        >
                          <option value="">Taux de TVA</option>
                          <option value="tva_55">5,5% (rénov. énergétique)</option>
                          <option value="tva_10">10% (travaux &gt;2 ans)</option>
                          <option value="tva_20">20% (neuf / commercial)</option>
                        </select>
                      </div>
                    </div>
                    <button
                      type="submit"
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-3.5 rounded-xl transition-colors shadow-lg hover:shadow-xl"
                    >
                      Voir nos tarifs →
                    </button>
                    <p className="text-xs text-slate-400 text-center">
                      ✓ Estimation instantanée — Sans email ni téléphone
                    </p>
                  </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TRUST STRIP ── */}
      <section className="bg-white border-b border-slate-100 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-8">
            {[
              /* { icon: '🏅', label: 'Certifié RGE' }, */
              /* { icon: '🔨', label: 'Qualibat 9111' }, */
              { icon: '🛡️', label: 'Garantie Décennale' },
              { icon: '⭐', label: '+10 ans d\'expérience' },
              { icon: '🔧', label: 'Tous corps de métier' },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-3">
                <span className="text-2xl">{item.icon}</span>
                <span className="font-semibold text-slate-700">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICES GRID ── */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-orange-600 font-semibold text-sm uppercase tracking-wider mb-2">Ce que nous faisons</p>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900">Nos prestations</h2>
            <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
              De la rénovation intérieure à l'aménagement de commerces, nous couvrons tous vos besoins avec des artisans qualifiés.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <Link
                key={service.slug}
                to={`/services/${service.slug}`}
                className="group bg-white rounded-xl p-6 shadow-sm hover:shadow-lg border border-transparent hover:border-orange-200 transition-all duration-300 hover:-translate-y-1"
              >
                <span className="text-4xl mb-4 block">{service.icon}</span>
                <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-orange-600 transition-colors">
                  {service.name}
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed mb-4 line-clamp-2">
                  {service.shortDesc}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">À partir de {service.price}€</span>
                  <span className="text-orange-600 text-sm font-semibold group-hover:translate-x-1 transition-transform inline-block">
                    En savoir plus →
                  </span>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 border-2 border-orange-600 text-orange-600 font-semibold px-8 py-3 rounded-xl hover:bg-orange-600 hover:text-white transition-all duration-200"
            >
              Voir toutes nos prestations
            </Link>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-orange-600 font-semibold text-sm uppercase tracking-wider mb-2">Simple &amp; transparent</p>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900">Comment ça marche ?</h2>
            <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
              Lancer votre projet de rénovation ne prend que quelques minutes. Nous gérons tout le reste.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 relative">
            <div className="hidden md:block absolute top-10 left-1/3 right-1/3 h-0.5 border-t-2 border-dashed border-orange-200" />
            {[
              {
                num: '01',
                icon: '📋',
                title: 'Devis en ligne gratuit',
                desc: 'Remplissez notre formulaire en 2 minutes. Décrivez votre projet, votre surface et vos coordonnées. C\'est entièrement gratuit et sans engagement.',
              },
              {
                num: '02',
                icon: '👷',
                title: 'Visite technique gratuite',
                desc: 'Sous 24h, un artisan qualifié se déplace pour évaluer votre chantier et vous remettre un devis détaillé. Visite gratuite, aucun frais caché.',
              },
              {
                num: '03',
                icon: '🏆',
                title: 'Travaux garantis 10 ans',
                desc: 'Après accord, nous planifions les travaux et les réalisons dans les délais convenus. Livraison clés en main avec garantie décennale incluse.',
              },
            ].map((step, idx) => (
              <div key={idx} className="text-center relative z-10">
                <div className="w-20 h-20 bg-orange-100 rounded-2xl flex items-center justify-center text-4xl mx-auto mb-6 relative">
                  {step.icon}
                  <span className="absolute -top-2 -right-2 w-7 h-7 bg-orange-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {idx + 1}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
                <p className="text-slate-600 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-14">
            <Link
              to="/devis"
              className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-bold px-6 py-3 sm:px-10 sm:py-4 rounded-xl transition-all shadow-lg text-sm sm:text-base"
            >
              Commencer mon projet →
            </Link>
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="bg-orange-600 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center text-white">
            {[
              { value: '50+', label: 'Chantiers réalisés' },
              { value: '4,8/5', label: 'Satisfaction client' },
              { value: '2025', label: 'Fondée à Châteauroux' },
              { value: '15+', label: 'Communes couvertes' },
            ].map(({ value, label }) => (
              <div key={label}>
                <p className="text-4xl lg:text-5xl font-extrabold mb-2">{value}</p>
                <p className="text-orange-100 font-medium">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── RECENT PROJECTS ── */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-orange-600 font-semibold text-sm uppercase tracking-wider mb-2">Notre portfolio</p>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900">Nos dernières réalisations</h2>
            </div>
            <Link to="/realisations" className="hidden sm:block text-sm font-semibold text-orange-600 hover:text-orange-700">
              Voir tout →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {recentProjects.map((project, idx) => (
              <div
                key={idx}
                className="group relative rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 aspect-[4/3]"
              >
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <span className="inline-block bg-orange-600 text-white text-xs font-semibold px-2.5 py-1 rounded-full mb-2">
                    {project.type}
                  </span>
                  <p className="text-white font-bold text-base leading-tight">{project.title}</p>
                  <p className="text-slate-300 text-sm">📍 {project.city}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/realisations"
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold px-8 py-3 rounded-xl transition-colors"
            >
              Voir toutes nos réalisations
            </Link>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-orange-600 font-semibold text-sm uppercase tracking-wider mb-2">Avis clients</p>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900">Ce que disent nos clients</h2>
            <div className="flex items-center justify-center gap-3 mt-4">
              <div className="flex">
                {'★★★★★'.split('').map((_, i) => (
                  <span key={i} className="text-amber-400 text-xl">★</span>
                ))}
              </div>
              <span className="font-semibold text-slate-700">4,8/5</span>
              <span className="text-slate-500">— Avis de nos clients Berry</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.slice(0, 3).map((t) => (
              <div key={t.id} className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                <div className="flex mb-4">
                  {'★★★★★'.split('').map((_, i) => (
                    <span key={i} className={`text-lg ${i < t.rating ? 'text-amber-400' : 'text-slate-200'}`}>★</span>
                  ))}
                </div>
                <p className="text-slate-600 text-sm leading-relaxed mb-5 italic">"{t.text}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                  <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-orange-700 text-sm font-bold flex-shrink-0">
                    {t.avatar}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">{t.name}</p>
                    <p className="text-slate-400 text-xs">{t.city} · {t.service}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BLOG PREVIEW ── */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-orange-600 font-semibold text-sm uppercase tracking-wider mb-2">Conseils &amp; guides</p>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900">Nos conseils rénovation</h2>
            </div>
            <Link to="/blog" className="hidden sm:block text-sm font-semibold text-orange-600 hover:text-orange-700">
              Tous les articles →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {blogArticles.slice(0, 3).map((article) => (
              <Link
                key={article.id}
                to={`/blog/${article.slug}`}
                className="group bg-white rounded-xl overflow-hidden border border-slate-200 hover:shadow-md transition-shadow"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <span className="inline-block bg-orange-100 text-orange-700 text-xs font-semibold px-2.5 py-1 rounded-full mb-3">
                    {article.category}
                  </span>
                  <h3 className="font-bold text-slate-900 text-base leading-snug mb-2 group-hover:text-orange-600 transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-slate-500 text-sm line-clamp-2 mb-3">{article.excerpt}</p>
                  <div className="flex items-center justify-between text-xs text-slate-400">
                    <span>{article.date}</span>
                    <span>{article.readTime} de lecture</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-orange-600 font-semibold text-sm uppercase tracking-wider mb-2">FAQ</p>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900">Questions fréquentes</h2>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto">
              Vous avez des questions sur nos services, certifications ou tarifs ? Retrouvez les réponses ci-dessous.
            </p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <FaqItem key={idx} q={faq.q} a={faq.a} />
            ))}
          </div>
          <p className="text-center text-slate-500 mt-8 text-sm">
            Vous ne trouvez pas votre réponse ?{' '}
            <Link to="/contact" className="text-orange-600 font-semibold hover:underline">
              Contactez-nous directement
            </Link>
          </p>
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section className="bg-gradient-to-r from-orange-600 to-amber-500 py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-extrabold text-white mb-4">
            Prêt à lancer votre projet ?
          </h2>
          <p className="text-orange-100 text-lg mb-10 max-w-2xl mx-auto">
            Obtenez un devis gratuit sous 24h. Nos artisans certifiés vous accompagnent de A à Z pour une rénovation réussie.
          </p>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 w-full sm:w-auto">
            <Link
              to="/devis"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 sm:px-10 sm:py-4 bg-white text-orange-600 font-bold rounded-xl hover:bg-orange-50 transition-all duration-200 shadow-lg text-sm sm:text-lg"
            >
              Réaliser mon devis gratuit →
            </Link>
            <a
              href="https://wa.me/33759748656"
              target="_blank"
              rel="noopener noreferrer"
              className="sm:hidden inline-flex items-center justify-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-bold rounded-xl transition-all duration-200 text-sm"
            >
              <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.126 1.533 5.858L.054 23.445a.75.75 0 00.922.922l5.587-1.479A11.94 11.94 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.907 0-3.7-.506-5.248-1.391l-.378-.22-3.917 1.036 1.036-3.917-.22-.378A9.952 9.952 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
              WhatsApp
            </a>
            {/* Desktop: Assistant IA */}
            <Link
              to="/assistant"
              className="hidden sm:inline-flex items-center gap-1.5 px-10 py-4 rounded-xl transition-all duration-200 text-lg font-semibold border text-white border-white/50 hover:bg-white/10"
            >
              ✦ Assistant IA
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
